/* Axiom Supply — email pop-up custom element (docs/email-popup.md)
   Subscribes via Klaviyo's client-side API. Optional by design:
   remembers dismissal, never nags, and offers four ways to exit. */
(function () {
  var KLAVIYO_REVISION = '2026-04-15';
  var STORAGE_KEY = 'axiom_popup_v1';

  function readState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch (e) { return {}; }
  }
  function writeState(patch) {
    var s = readState();
    for (var k in patch) s[k] = patch[k];
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', '');
      ta.style.position = 'absolute'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    } catch (e) {}
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

  class AxiomEmailPopup extends HTMLElement {
    connectedCallback() {
      this.company = this.dataset.company || '';
      this.listId = this.dataset.list || '';
      this.trigger = this.dataset.trigger || 'time';
      this.delay = parseInt(this.dataset.delay, 10);
      if (isNaN(this.delay)) this.delay = 6;

      this.scrim = this.querySelector('[data-scrim]');
      this.card = this.querySelector('.axiom-popup__card');
      this.form = this.querySelector('[data-form]');
      this.emailInput = this.querySelector('[data-email]');
      this.errorEl = this.querySelector('[data-error]');
      this.submitBtn = this.querySelector('.axiom-popup__submit');
      this.opened = false;
      this.done = false;
      // Force-show for testing: ?axiompopup=1 in the URL, or inside the Shopify theme editor.
      this.force = /[?&]axiompopup=1/i.test(location.search) || !!(window.Shopify && window.Shopify.designMode);
      // Force-suppress for QA screenshots: ?axiompopup=0 (never affects real traffic).
      if (/[?&]axiompopup=0/i.test(location.search)) return;

      this.bind();
      if (this.force) { this.schedule(); return; }
      if (!this.shouldShow()) return;
      this.schedule();
    }

    shouldShow() {
      var s = readState();
      // Never re-show once the visitor has subscribed or closed it (permanent, per browser).
      if (s.subscribed || s.dismissed) return false;
      // Also once per page-view session (avoid re-pop after in-tab navigation).
      try { if (sessionStorage.getItem('axiom_popup_seen')) return false; } catch (e) {}
      return true;
    }

    bind() {
      var self = this;
      this.querySelectorAll('[data-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close('dismiss'); });
      });
      // Click outside the card (on the scrim) closes.
      this.scrim.addEventListener('mousedown', function (e) {
        if (e.target === self.scrim) self.close('dismiss');
      });
      // ESC closes.
      this._onKey = function (e) {
        if (e.key === 'Escape' && self.opened) { self.close('dismiss'); return; }
        if (e.key === 'Tab' && self.opened) self.trapFocus(e);
      };
      document.addEventListener('keydown', this._onKey);
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

      // Copy the discount code shown on the success screen.
      var copyBtn = this.querySelector('[data-copy]');
      var codeEl = this.querySelector('[data-code]');
      if (copyBtn && codeEl) {
        copyBtn.addEventListener('click', function () {
          var code = (codeEl.textContent || '').trim();
          var label = copyBtn.dataset.label || copyBtn.textContent;
          copyBtn.dataset.label = label;
          var done = function () {
            copyBtn.textContent = 'Copied!';
            copyBtn.classList.add('is-copied');
            setTimeout(function () { copyBtn.textContent = label; copyBtn.classList.remove('is-copied'); }, 1800);
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done).catch(function () { fallbackCopy(code); done(); });
          } else { fallbackCopy(code); done(); }
        });
      }
    }

    schedule() {
      var self = this;
      if (this.force) {
        this._timer = setTimeout(function () { if (!self.done) self.open(); }, 400);
        return;
      }
      if (this.trigger === 'exit_intent') {
        this._onMouseOut = function (e) {
          if (e.clientY <= 0 && !self.opened && !self.done) self.open();
        };
        document.addEventListener('mouseout', this._onMouseOut);
        // Fallback so mobile / no-exit visitors still see it.
        this._timer = setTimeout(function () { if (!self.opened && !self.done) self.open(); }, Math.max(this.delay, 15) * 1000);
      } else {
        this._timer = setTimeout(function () { if (!self.done) self.open(); }, this.delay * 1000);
      }
    }

    open() {
      if (this.opened) return;
      this.opened = true;
      if (!this.force) { try { sessionStorage.setItem('axiom_popup_seen', '1'); } catch (e) {} }
      this._lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      // Land focus on the card so the first Tab reaches the Close button.
      var self = this;
      requestAnimationFrame(function () { self.card.focus(); });
    }

    close(reason) {
      if (!this.opened) return;
      this.opened = false;
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (reason === 'dismiss' && !this.force) writeState({ dismissed: true });
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    trapFocus(e) {
      var nodes = Array.prototype.filter.call(
        this.card.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null; }
      );
      if (!nodes.length) return;
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    showError(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
    }
    clearError() {
      this.errorEl.hidden = true;
      this.emailInput.removeAttribute('aria-invalid');
    }

    onSubmit(e) {
      e.preventDefault();
      this.clearError();
      var email = (this.emailInput.value || '').trim();
      if (!email || !this.emailInput.checkValidity()) {
        this.showError('Please enter a valid email address.');
        this.emailInput.focus();
        return;
      }
      if (!this.company || !this.listId) {
        this.showError('Sign-up isn’t configured yet — please try again later.');
        if (window.console) console.warn('[axiom-popup] Missing Klaviyo company_id or list_id — set them in the theme editor.');
        return;
      }
      this.subscribe(email);
    }

    subscribe(email) {
      var self = this;
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Signing you up…';

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } }
        }
      };

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (res.ok) { self.onSuccess(); }
        else { throw new Error('HTTP ' + res.status); }
      }).catch(function (err) {
        if (window.console) console.warn('[axiom-popup] subscribe failed:', err);
        self.submitBtn.disabled = false;
        self.submitBtn.textContent = self.submitBtn.dataset.label || 'Sign me up';
        self.showError('Something went wrong — please try again.');
      });
    }

    onSuccess() {
      writeState({ subscribed: true });
      this.done = true;
      this.card.classList.add('is-done');
      this.querySelector('[data-success]').hidden = false;
      // If a code is shown, keep the pop-up open so they can copy it (close button stays available).
      if (!this.querySelector('[data-code]')) {
        var self = this;
        this._timer = setTimeout(function () { self.close('subscribed'); }, 2800);
      }
    }

    disconnectedCallback() {
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._onMouseOut) document.removeEventListener('mouseout', this._onMouseOut);
      if (this._timer) clearTimeout(this._timer);
    }
  }

  if (!customElements.get('axiom-email-popup')) {
    customElements.define('axiom-email-popup', AxiomEmailPopup);
  }
})();
